import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  CircularProgress
} from "@mui/material";
import toast from "react-hot-toast";
import {
  PayPalCardFieldsProvider,
  PayPalNumberField,
  PayPalExpiryField,
  PayPalCVVField,
  usePayPalCardFields,
} from "@paypal/react-paypal-js";
import { getToken } from '../utils/cookieUtils';

const CardFieldsModal = ({ show, onClose, onCardSaved }) => {
  const [billingDetails, setBillingDetails] = useState({
    first_name: "",
    last_name: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country_code: "US",
  });
  const [loading, setLoading] = useState(false);
  const [cardFieldsInitialized, setCardFieldsInitialized] = useState(false);

  const { cardFields } = usePayPalCardFields();

  useEffect(() => {
    if (cardFields) {
      setCardFieldsInitialized(true);
    }
  }, [cardFields]);

  const handleBillingDetailsChange = (e) => {
    setBillingDetails({ ...billingDetails, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!cardFieldsInitialized) {
      toast.error("Card fields are not ready. Please try again.");
      return;
    }

    // Validate required fields
    const requiredFields = ['first_name', 'last_name', 'address', 'city', 'state', 'postal_code'];
    const missingFields = requiredFields.filter(field => !billingDetails[field]);
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      // First, create a billing agreement
      const result = await cardFields.submit({
        cardholderName: `${billingDetails.first_name} ${billingDetails.last_name}`,
        billingAddress: {
          addressLine1: billingDetails.address,
          city: billingDetails.city,
          state: billingDetails.state,
          postalCode: billingDetails.postal_code,
          countryCode: billingDetails.country_code
        }
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      console.log("Card fields submission result:", result);

      // Save card details to your backend
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}user/add_card_to_vault`, {
        method: "POST",
        headers: {
          "Authorization": "Bearer " + getToken(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          type: "card",
          first_name: billingDetails.first_name,
          last_name: billingDetails.last_name,
          address: billingDetails.address,
          city: billingDetails.city,
          state: billingDetails.state,
          postal_code: billingDetails.postal_code,
          country_code: billingDetails.country_code,
          user_id: localStorage.getItem("logged_user_id"),
          vault_id: result.vaultId || result.id // Handle both possible response formats
        })
      });

      const data = await response.json();
      console.log("Backend response:", data);

      if (!data.status) {
        throw new Error(data.message || "Failed to save card details");
      }

      // Save vault ID to localStorage
      if (data.vault_id) {
        localStorage.setItem("paypal_vault_id", data.vault_id);
      }

      toast.success("Card saved successfully!");
      onCardSaved(data);
      onClose();
    } catch (error) {
      console.error("Card submission error:", error);
      toast.error(error.message || "Failed to save card details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cardFieldStyle = {
    input: {
      fontSize: "16px",
      fontFamily: "Manrope, sans-serif",
      color: "#3A3A3A",
      padding: "12px",
      backgroundColor: "#FFFFFF",
      borderRadius: "4px",
      border: "1px solid #E0E0E0",
      width: "100%"
    },
    ".invalid": {
      color: "#FF5E41"
    }
  };

  return (
    <Dialog open={show} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enter Card Details</DialogTitle>
      <DialogContent>
        <PayPalCardFieldsProvider
          createVaultSetupToken={(data, actions) => {
            // Call your server to set up the vault
            // Replace '/api/setup-vault' with your actual endpoint
            return fetch(`${process.env.REACT_APP_BASE_URL}user/setup-paypal-vault`, {
              method: 'post',
              headers: {
                Authorization: "Bearer " + getToken(),
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                  user_id: localStorage.getItem("logged_user_id"),
                  // Include any other necessary data for vault setup
              })
            }).then(function(res) {
              return res.json();
            }).then(function(data) {
              if (!data.status) {
                throw new Error(data.message || "Failed to set up PayPal vault");
              }
              return data.vaultSetupToken;
            });
          }}
          onError={(err) => {
            console.error("PayPal error:", err);
            toast.error("Failed to process card. Please try again.");
          }}
        >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Card Number
              </Typography>
              <PayPalNumberField style={cardFieldStyle} />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                Expiry Date
              </Typography>
              <PayPalExpiryField style={cardFieldStyle} />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                CVV
              </Typography>
              <PayPalCVVField style={cardFieldStyle} />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="First Name"
                name="first_name"
                value={billingDetails.first_name}
                onChange={handleBillingDetailsChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Last Name"
                name="last_name"
                value={billingDetails.last_name}
                onChange={handleBillingDetailsChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                name="address"
                value={billingDetails.address}
                onChange={handleBillingDetailsChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="City"
                name="city"
                value={billingDetails.city}
                onChange={handleBillingDetailsChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="State"
                name="state"
                value={billingDetails.state}
                onChange={handleBillingDetailsChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Postal Code"
                name="postal_code"
                value={billingDetails.postal_code}
                onChange={handleBillingDetailsChange}
                fullWidth
                required
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <img 
              src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg"
              alt="PayPal Logo"
              style={{ height: 23 }} 
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Securely processed by PayPal
            </Typography>
          </Box>
        </PayPalCardFieldsProvider>
      </DialogContent>
      <DialogActions>
        <button onClick={onClose} color="secondary" disabled={loading}>
          Cancel
        </button>
        <button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || !cardFieldsInitialized}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Processing...
            </>
          ) : (
            "Save Card"
          )}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default CardFieldsModal;